/**
 * Validation for the SOP "policy" reference link on the generate screen.
 *
 * The policy portal publishes documents at a fixed URL template:
 *   https://policy.a2z.com/docs/<numericId>/publication
 * A valid link must match this shape exactly: the `docs` segment, a numeric
 * document id, then the `publication` segment. A link that matches is a
 * well-formed, openable published-policy reference.
 */

/** Required scheme — the link is rendered into an anchor href, so only https. */
const REQUIRED_PROTOCOL = 'https:';

/** The policy portal host the document id lives under. */
const POLICY_HOST = 'policy.a2z.com';

/** Leading path segment before the numeric document id. */
const DOCS_SEGMENT = 'docs';

/** Trailing path segment that marks the published policy document. */
const PUBLICATION_SEGMENT = 'publication';

/**
 * True when `raw` is a published policy document link:
 * `https://policy.a2z.com/docs/<id>/publication` where `<id>` is one or more
 * digits. The `/publication` segment is REQUIRED. A trailing slash is
 * tolerated; a query string / hash is ignored. Parsed with `new URL()` so a
 * non-URL, a wrong scheme (`javascript:`, `http:`), or a different host is
 * rejected rather than matched by a loose string check.
 */
export function isValidPolicyUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== REQUIRED_PROTOCOL) return false;
  if (url.hostname !== POLICY_HOST) return false;
  // Path segments, dropping the empty strings from the leading slash and any
  // trailing slash: must be exactly ['docs', '<id>', 'publication'].
  const segments = url.pathname.split('/').filter((s) => s.length > 0);
  if (segments.length !== 3) return false;
  const [docs, id, publication] = segments;
  if (docs !== DOCS_SEGMENT) return false;
  if (publication !== PUBLICATION_SEGMENT) return false;
  // Digits-only id is required; `/^\d+$/` rejects empty, non-numeric, and
  // mixed segments.
  return id !== undefined && /^\d+$/.test(id);
}
